import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  UserRoundSearch,
  MessageSquare,
  Sparkles,
  Loader2,
  Save,
  RefreshCw,
  BookOpen,
  Trash2,
  FileText,
  Stethoscope,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SixDimRadar } from '@/components/six-dim-radar';
import {
  getPersona,
  getExpression,
  saveExpression,
  getMemory,
  saveMemory,
  importPersona,
  importPersonaFromDiagnosis,
  diagnosisMyRuns,
  clearPersona,
  getRewriteConfig,
  saveRewriteConfig,
  extractExpression,
  extractExpressionStatus,
  transcribeAllExpression,
  transcribeAllExpressionStatus,
  extractTagsLlm,
  extractTagsLlmStatus,
  type DiagnosisRun,
} from '@/api/client';

type PersonaData = {
  nickname: string;
  uid: string;
  profile: any;
  video_count: number;
  total_digg: number;
  avg_digg: number;
  top_hashtags: {tag: string; count: number}[];
  account_keywords: {word: string; count: number}[];
  recent_topics: any[];
  expression: string;
  memory: string;
  rewrite_config: any;
  analyzed_at: string | null;
  library_match: { matched: number; transcribed: number };
  library_tags: {tag: string; count: number}[];
  dimensions: Record<string, any>;
};

// 数字格式化：123456 → 12.3万
function fmt(n: number): string {
  if (!n && n !== 0) return '0';
  if (n >= 100000000) return (n / 100000000).toFixed(1) + '亿';
  if (n >= 10000) return (n / 10000).toFixed(1) + '万';
  return String(n);
}

// 体检报告平台/档位显示名
const PLATFORM_LABEL: Record<string, string> = {
  xiaohongshu: '小红书',
  douyin: '抖音',
  wechat: '视频号',
};
const TIER_NAME: Record<string, string> = {
  basic: '基础版',
  enhanced: '增强版',
  deep: '深度版',
};

export default function PersonaPage() {
  const navigate = useNavigate();
  const [persona, setPersona] = React.useState<PersonaData | null>(null);
  const [expression, setExpressionState] = React.useState('');
  const [memory, setMemoryState] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [savingExp, setSavingExp] = React.useState(false);
  const [savingMem, setSavingMem] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  const [clearing, setClearing] = React.useState(false);
  const [importResult, setImportResult] = React.useState('');
  const [importUrl, setImportUrl] = React.useState('');
  const [showImportInput, setShowImportInput] = React.useState(false);
  const [showReportPicker, setShowReportPicker] = React.useState(false);
  const [reports, setReports] = React.useState<DiagnosisRun[]>([]);
  const [reportsLoading, setReportsLoading] = React.useState(false);
  const [reportImportingId, setReportImportingId] = React.useState<number | null>(null);
  const [activeTab, setActiveTab] = React.useState<'profile' | 'expression' | 'memory' | 'rewrite'>('profile');

  // 表达习惯自动提取（从本人账号作品）
  const [extracting, setExtracting] = React.useState(false);
  const [extractMsg, setExtractMsg] = React.useState('');
  const [extractJobId, setExtractJobId] = React.useState('');
  // 逐字稿不足 → 一键批量转录（含进度条）
  const [needTranscript, setNeedTranscript] = React.useState(false);
  const [transcribingAll, setTranscribingAll] = React.useState(false);
  const [transcribeMsg, setTranscribeMsg] = React.useState('');
  const [transcribeProgress, setTranscribeProgress] = React.useState<{ done: number; total: number } | null>(null);

  // 改写参数配置
  const [rw, setRw] = React.useState<Record<string, any>>({});
  const [rwLoading, setRwLoading] = React.useState(false);
  const [rwSaving, setRwSaving] = React.useState(false);
  const [rwSaved, setRwSaved] = React.useState('');

  // LLM 标签提取（2026-09-15）
  const [tagExtracting, setTagExtracting] = React.useState(false);
  const [tagExtractMsg, setTagExtractMsg] = React.useState('');
  const [tagExtractProgress, setTagExtractProgress] = React.useState<{ done: number; total: number } | null>(null);

  const loadRewriteConfig = React.useCallback(async () => {
    setRwLoading(true);
    try {
      const c = await getRewriteConfig();
      setRw(c || {});
    } catch {
      // ignore
    } finally {
      setRwLoading(false);
    }
  }, []);

  const handleSaveRewrite = React.useCallback(async () => {
    setRwSaving(true);
    setRwSaved('');
    try {
      await saveRewriteConfig(rw);
      setRwSaved('已保存，改写时立即生效');
      setTimeout(() => setRwSaved(''), 2500);
    } catch {
      setRwSaved('保存失败');
      setTimeout(() => setRwSaved(''), 2500);
    } finally {
      setRwSaving(false);
    }
  }, [rw]);

  const handleResetRewrite = React.useCallback(async () => {
    const defaults = {
      temperature: 0.7, max_chars: 2000, length_mode: 'similar',
      opening_mode: 'keep', opening_seconds: 3, originality: 70,
      use_expression: true, use_ip_memory: true, casual_style: true,
      use_hashtags: true, keep_paragraphs: true,
    };
    setRw(defaults);
    setRwSaving(true);
    setRwSaved('');
    try {
      await saveRewriteConfig(defaults);
      setRwSaved('已恢复默认');
      setTimeout(() => setRwSaved(''), 2500);
    } catch {
      setRwSaved('保存失败');
      setTimeout(() => setRwSaved(''), 2500);
    } finally {
      setRwSaving(false);
    }
  }, []);

  const setRwField = React.useCallback((key: string, value: any) => {
    setRw((prev) => ({ ...prev, [key]: value }));
  }, []);

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    try {
      const [p, expr, mem] = await Promise.all([
        getPersona(),
        getExpression(),
        getMemory(),
      ]);
      setPersona(p);
      setExpressionState(expr.expression || '');
      setMemoryState(mem.memory || '');
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ─── LLM 标签提取（2026-09-15）───────────────────────────────

  const handleExtractTagsLlm = React.useCallback(async () => {
    if (tagExtracting) return;
    setTagExtracting(true);
    setTagExtractMsg('正在准备提取…');
    setTagExtractProgress(null);
    try {
      const data = await extractTagsLlm();
      if (!data.job_id) {
        setTagExtracting(false);
        setTagExtractMsg(data.message || '暂无可分析的已转录作品');
        return;
      }
      const timer = window.setInterval(async () => {
        try {
          const st = await extractTagsLlmStatus(data.job_id);
          if (st.videos_total) {
            setTagExtractProgress({ done: st.videos_done ?? 0, total: st.videos_total });
          }
          if (st.status === 'done' || st.status === 'error') {
            window.clearInterval(timer);
            setTagExtracting(false);
            setTagExtractProgress(null);
            if (st.status === 'done') {
              setTagExtractMsg(st.message || '标签提取完成');
              loadAll();
            } else {
              setTagExtractMsg(`提取失败：${st.error || st.message || '未知错误'}`);
            }
            return;
          }
          setTagExtractMsg(st.message || '提取中…');
        } catch {
          window.clearInterval(timer);
          setTagExtracting(false);
          setTagExtractProgress(null);
          setTagExtractMsg('进度查询失败，请稍后重试');
        }
      }, 2000);
    } catch (err) {
      setTagExtracting(false);
      setTagExtractMsg(err instanceof Error ? err.message : '提取启动失败，请稍后重试');
    }
  }, [tagExtracting, loadAll]);

  // ─── 保存表达 ────────────────────────────────────────────────

  const handleSaveExpression = React.useCallback(async () => {
    setSavingExp(true);
    try {
      await saveExpression(expression);
    } catch {
      // ignore
    } finally {
      setSavingExp(false);
    }
  }, [expression]);

  // ─── 保存价值观 ──────────────────────────────────────────────

  const handleSaveMemory = React.useCallback(async () => {
    setSavingMem(true);
    try {
      await saveMemory(memory);
    } catch {
      // ignore
    } finally {
      setSavingMem(false);
    }
  }, [memory]);

  // ─── 导入 ────────────────────────────────────────────────────

  const handleImport = React.useCallback(async () => {
    if (!importUrl.trim()) {
      setImportResult('请先输入抖音分享链接或主页链接');
      return;
    }
    setImporting(true);
    setImportResult('');
    try {
      const data = await importPersona(importUrl.trim());
      setImportResult(data.message || '导入成功');
      setShowImportInput(false);
      if (data.extract_job_id) {
        setExtractJobId(data.extract_job_id);
        setExtractMsg('正在后台自动分析表达习惯与IP价值观，可切到「表达习惯」页查看进度…');
      }
      loadAll();
    } catch (err) {
      setImportResult(err instanceof Error ? err.message : '导入失败，请检查链接后重试');
    } finally {
      setImporting(false);
    }
  }, [importUrl, loadAll]);

  // ─── 从体检报告导入 ───────────────────────────────────────────

  const handleOpenReportPicker = React.useCallback(async () => {
    if (showReportPicker) {
      setShowReportPicker(false);
      return;
    }
    setShowImportInput(false);
    setShowReportPicker(true);
    setReportsLoading(true);
    try {
      const data = await diagnosisMyRuns();
      setReports((data.runs || []).filter((r) =>
        ['completed', 'partial', 'settled'].includes(r.status)
      ));
    } catch {
      setReports([]);
      setImportResult('加载体检报告失败，请稍后重试');
    } finally {
      setReportsLoading(false);
    }
  }, [showReportPicker]);

  const handleImportFromReport = React.useCallback(async (runId: number) => {
    setReportImportingId(runId);
    setImportResult('');
    try {
      const data = await importPersonaFromDiagnosis(runId);
      setImportResult(data.message || '导入成功');
      setShowReportPicker(false);
      if (data.extract_job_id) {
        setExtractJobId(data.extract_job_id);
        setExtractMsg('正在后台自动分析表达习惯与IP价值观，可切到「表达习惯」页查看进度…');
      }
      loadAll();
    } catch (err) {
      setImportResult(err instanceof Error ? err.message : '导入失败，请稍后重试');
    } finally {
      setReportImportingId(null);
    }
  }, [loadAll]);

  // ─── 清空 ────────────────────────────────────────────────────

  const handleClearPersona = React.useCallback(async () => {
    if (!window.confirm('确定清空你的 IP 人设数据吗？清空后需要重新导入账号并分析。')) {
      return;
    }
    setClearing(true);
    try {
      await clearPersona();
      await loadAll();
    } catch {
      // ignore
    } finally {
      setClearing(false);
    }
  }, [loadAll]);

  // ─── 从本人账号提取表达习惯/IP价值观（自动分析 + 进度轮询） ──

  const handleExtractExpression = React.useCallback(async () => {
    if (extracting) return;
    setExtracting(true);
    setExtractMsg('');
    setNeedTranscript(false);
    try {
      const data = await extractExpression();
      setExtractJobId(data.job_id);
      setExtractMsg('正在准备分析…');
      // 轮询进度（与监控/转录一致：1.5s 间隔）
      const timer = window.setInterval(async () => {
        try {
          const st = await extractExpressionStatus(data.job_id);
          if (st.status === 'done' || st.status === 'error') {
            window.clearInterval(timer);
            setExtracting(false);
            setExtractJobId('');
            if (st.status === 'done') {
              setExtractMsg('分析完成！表达习惯与IP价值观已自动填充');
              await loadAll();
            } else if (st.code === 'need_transcript') {
              // 逐字稿不足：弹出提醒，引导一键转录当前账号作品
              setNeedTranscript(true);
              setExtractMsg(`当前账号仅有 ${st.have_transcript ?? 0} 条可用逐字稿（需要至少 2 条），转录后即可自动完成分析`);
            } else {
              setExtractMsg(`分析失败：${st.error || st.message || '未知错误'}`);
            }
            return;
          }
          setExtractMsg(st.message || '分析中…');
          if (st.videos_total) {
            setExtractMsg(`${st.message || '分析中…'}（${st.videos_done ?? 0}/${st.videos_total}）`);
          }
        } catch {
          window.clearInterval(timer);
          setExtracting(false);
          setExtractJobId('');
          setExtractMsg('进度查询失败，请稍后重新提取');
        }
      }, 1500);
    } catch (err) {
      setExtracting(false);
      setExtractJobId('');
      setExtractMsg(err instanceof Error ? err.message : '提取失败，请稍后重试');
    }
  }, [extracting, loadAll]);

  // ─── 一键转录当前账号作品（逐字稿不足时） → 完成后自动衔接分析 ──

  const handleTranscribeAll = React.useCallback(async () => {
    if (transcribingAll) return;
    setTranscribingAll(true);
    setNeedTranscript(false);
    setTranscribeMsg('正在准备转录…');
    setTranscribeProgress(null);
    try {
      const data = await transcribeAllExpression();
      const timer = window.setInterval(async () => {
        try {
          const st = await transcribeAllExpressionStatus(data.job_id);
          if (st.videos_total) {
            setTranscribeProgress({ done: st.videos_done ?? 0, total: st.videos_total });
          }
          if (st.status === 'done' || st.status === 'error') {
            window.clearInterval(timer);
            setTranscribingAll(false);
            setTranscribeProgress(null);
            if (st.status === 'error') {
              setTranscribeMsg(`转录失败：${st.error || st.message || '未知错误'}`);
              return;
            }
            setTranscribeMsg('转录完成，正在自动分析表达习惯…');
            // 转录完成后后端自动启动了分析任务，继续轮询直到分析完成
            const followup = st.followup_job_id;
            if (!followup) {
              setTranscribeMsg('转录完成，但未启动分析，请再次点击「从本人账号中提取」');
              await loadAll();
              return;
            }
            const exprTimer = window.setInterval(async () => {
              try {
                const es = await extractExpressionStatus(followup);
                if (es.status === 'done' || es.status === 'error') {
                  window.clearInterval(exprTimer);
                  if (es.status === 'done') {
                    setTranscribeMsg('转录与分析全部完成，表达习惯与IP价值观已自动填充');
                  } else if (es.code === 'need_transcript') {
                    setNeedTranscript(true);
                    setTranscribeMsg(`分析仍缺少逐字稿（${es.have_transcript ?? 0} 条），转录可能未全部成功`);
                  } else {
                    setTranscribeMsg(`分析失败：${es.error || es.message || '未知错误'}`);
                  }
                  await loadAll();
                } else {
                  setTranscribeMsg(es.message || '正在分析表达习惯…');
                }
              } catch {
                window.clearInterval(exprTimer);
                setTranscribeMsg('分析进度查询失败，请稍后重新提取');
              }
            }, 1500);
          } else {
            setTranscribeMsg(st.message || '正在转录…');
          }
        } catch {
          window.clearInterval(timer);
          setTranscribingAll(false);
          setTranscribeProgress(null);
          setTranscribeMsg('转录进度查询失败，请稍后重试');
        }
      }, 1500);
    } catch (err) {
      setTranscribingAll(false);
      setTranscribeMsg(err instanceof Error ? err.message : '转录启动失败，请稍后重试');
    }
  }, [transcribingAll, loadAll]);

  // 导入后自动触发的提取任务：进入表达习惯页时若仍在运行则继续轮询
  React.useEffect(() => {
    if (!extractJobId || extracting) return;
    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const st = await extractExpressionStatus(extractJobId);
        if (st.status === 'done' || st.status === 'error') {
          window.clearInterval(timer);
          if (cancelled) return;
          setExtractJobId('');
          if (st.status === 'done') {
            setExtractMsg('分析完成！表达习惯与IP价值观已自动填充');
            await loadAll();
          } else {
            setExtractMsg(`自动分析未完成：${st.error || st.message || '未知错误'}`);
          }
        } else {
          if (!cancelled) setExtractMsg(st.message || '正在后台分析…');
        }
      } catch {
        window.clearInterval(timer);
        if (!cancelled) setExtractJobId('');
      }
    }, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [extractJobId, extracting, loadAll]);

  // ─── Tab 切换 ────────────────────────────────────────────────

  const tabs = [
    { key: 'profile' as const, label: '个人画像', icon: User },
    { key: 'expression' as const, label: '表达习惯', icon: MessageSquare },
    { key: 'memory' as const, label: 'IP价值观', icon: BookOpen },
    { key: 'rewrite' as const, label: '内容撰写设置', icon: Sparkles },
  ];

  // 切换 tab 时按需加载改写配置
  React.useEffect(() => {
    if (activeTab === 'rewrite' && Object.keys(rw).length === 0 && !rwLoading) {
      loadRewriteConfig();
    }
  }, [activeTab, rw, rwLoading, loadRewriteConfig]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 lg:grid-cols-3">
          <Skeleton className="h-48 rounded-xl lg:col-span-2" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">我的账号</h1>
          <p className="mt-1 text-sm text-muted-foreground">个人画像、表达习惯、IP价值观和内容撰写设置</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearPersona}
            disabled={clearing}
            className="text-destructive hover:text-destructive"
          >
            {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {clearing ? '清空中…' : '清空'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/diagnosis')}
            className="relative border-dashed border-green-400 text-green-600 hover:text-green-600 dark:text-green-400"
          >
            <Stethoscope className="h-4 w-4" />
            账号体检
            <span className="ml-0.5 rounded border border-dashed border-green-400 px-1 text-[9px] font-bold leading-[14px]">新</span>
          </Button>
        </div>
      </div>

      {/* Tab 导航 */}
      <div className="mb-6 flex gap-1 rounded-lg border bg-muted/30 p-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── 个人画像 ─── */}
      {activeTab === 'profile' && (
        <div className="space-y-6">
          {/* 个人资料头部 */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start gap-5">
                {persona?.profile?.avatar ? (
                  <img
                    src={persona.profile.avatar}
                    alt={persona.nickname || '头像'}
                    className="h-20 w-20 shrink-0 rounded-full border object-cover"
                    referrerPolicy="no-referrer"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border border-primary/10 bg-gradient-to-br from-primary/15 via-chart-3/15 to-chart-4/15">
                    {persona?.nickname ? (
                      <span className="text-2xl font-bold text-primary">{persona.nickname[0]}</span>
                    ) : (
                      <User className="h-9 w-9 text-primary/50" />
                    )}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xl font-semibold">{persona?.nickname || '未设置'}</span>
                    {persona?.profile?.short_id && (
                      <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                        抖音号：{persona.profile.short_id}
                      </span>
                    )}
                    {persona?.profile?.ip_location && (
                      <span className="text-xs text-muted-foreground">{persona.profile.ip_location}</span>
                    )}
                    {typeof persona?.profile?.user_age === 'number' && persona.profile.user_age > 0 && (
                      <span className="text-xs text-muted-foreground">年龄 {persona.profile.user_age}</span>
                    )}
                  </div>
                  {persona?.profile?.signature && (
                    <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{persona.profile.signature}</p>
                  )}
                  {!persona?.nickname && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      尚未导入个人资料，请粘贴抖音/小红书/视频号分享链接，或从体检报告中导入
                    </p>
                  )}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <Button
                      onClick={() => { setShowImportInput(v => !v); setShowReportPicker(false); }}
                      disabled={importing}
                      variant="outline"
                      size="sm"
                    >
                      {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                      {importing ? '分析中…' : '导入账号数据'}
                    </Button>
                    <Button
                      onClick={handleOpenReportPicker}
                      disabled={reportsLoading || reportImportingId !== null}
                      variant="outline"
                      size="sm"
                    >
                      {reportsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                      从体检报告中导入
                    </Button>
                    {showImportInput && (
                      <div className="flex w-full max-w-xl items-center gap-2">
                        <input
                          value={importUrl}
                          onChange={(e) => setImportUrl(e.target.value)}
                          placeholder="粘贴抖音/小红书/视频号分享链接，智能识别"
                          className="h-8 flex-1 rounded-md border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        />
                        <Button onClick={handleImport} disabled={importing} size="sm">
                          {importing ? '分析中…' : '开始分析'}
                        </Button>
                      </div>
                    )}
                    {showReportPicker && (
                      <div className="w-full max-w-xl space-y-2 rounded-lg border bg-background p-3">
                        <div className="text-sm font-medium">选择一份体检报告</div>
                        {reportsLoading ? (
                          <div className="space-y-2">
                            <Skeleton className="h-10 w-full" />
                            <Skeleton className="h-10 w-full" />
                          </div>
                        ) : reports.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            暂无已完成的体检报告，请先到「账号体检」创建体检。
                          </p>
                        ) : (
                          <div className="max-h-64 space-y-1.5 overflow-y-auto">
                            {reports.map((r) => (
                              <button
                                key={r.id}
                                onClick={() => handleImportFromReport(r.id)}
                                disabled={reportImportingId !== null}
                                className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors hover:bg-accent/50 disabled:opacity-60"
                              >
                                <span className="flex min-w-0 items-center gap-2">
                                  <Badge variant="outline" className="shrink-0 text-xs">
                                    {PLATFORM_LABEL[r.platform] || r.platform}
                                  </Badge>
                                  <span className="truncate font-medium">
                                    {r.account_name || `报告 #${r.id}`}
                                  </span>
                                </span>
                                <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                                  <span>{TIER_NAME[r.tier] || r.tier}</span>
                                  <span>·</span>
                                  <span>{(r.created_at || '').slice(0, 10)}</span>
                                  {reportImportingId === r.id && (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  )}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                          导入将用报告中的账号资料与作品数据更新 IP 画像
                        </p>
                      </div>
                    )}
                    {importResult && (
                      <span className="inline-flex items-center text-sm text-muted-foreground">{importResult}</span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 数据指标：无账号时引导卡，有账号时图表色数字 */}
          {persona?.nickname ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              <div className="rounded-lg border p-3 text-center transition-all hover:-translate-y-0.5 hover:shadow-sm">
                <div className="text-xl font-bold text-chart-1">{fmt(persona?.profile?.followers ?? 0)}</div>
                <div className="text-xs text-muted-foreground">粉丝数</div>
              </div>
              <div className="rounded-lg border p-3 text-center transition-all hover:-translate-y-0.5 hover:shadow-sm">
                <div className="text-xl font-bold text-chart-2">{fmt(persona?.profile?.total_favorited ?? 0)}</div>
                <div className="text-xs text-muted-foreground">总获赞</div>
              </div>
              <div className="rounded-lg border p-3 text-center transition-all hover:-translate-y-0.5 hover:shadow-sm">
                <div className="text-xl font-bold text-chart-3">{fmt(persona?.profile?.aweme_count ?? persona?.video_count ?? 0)}</div>
                <div className="text-xs text-muted-foreground">总作品数</div>
              </div>
              <div className="rounded-lg border p-3 text-center transition-all hover:-translate-y-0.5 hover:shadow-sm">
                <div className="text-xl font-bold text-chart-4">{persona?.video_count ?? 0}</div>
                <div className="text-xs text-muted-foreground">已分析作品</div>
              </div>
              <div className="rounded-lg border p-3 text-center transition-all hover:-translate-y-0.5 hover:shadow-sm">
                <div className="text-xl font-bold text-chart-5">{fmt(persona?.total_digg ?? 0)}</div>
                <div className="text-xs text-muted-foreground">分析获赞</div>
              </div>
              <div className="rounded-lg border p-3 text-center transition-all hover:-translate-y-0.5 hover:shadow-sm">
                <div className="text-xl font-bold text-chart-1">{persona?.avg_digg ?? 0}</div>
                <div className="text-xs text-muted-foreground">平均赞/条</div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border-2 border-dashed border-primary/20 bg-gradient-to-br from-primary/[0.04] to-chart-3/[0.06] p-8 text-center">
              <UserRoundSearch className="mx-auto h-10 w-10 text-primary/50" />
              <div className="mt-3 text-sm font-medium">导入账号后，这里将展示粉丝、获赞、作品与互动数据</div>
              <div className="mt-1 text-xs text-muted-foreground">支持抖音 / 小红书 / 视频号分享链接，或从体检报告导入</div>
              <Button
                onClick={() => setShowImportInput(v => !v)}
                variant="outline"
                size="sm"
                className="mt-4"
              >
                <RefreshCw className="h-3.5 w-3.5" /> 去导入账号
              </Button>
            </div>
          )}

          <div className="grid gap-6 lg:grid-cols-3">
            {/* 账号标签：内容关键词 + 内容标签 + 内容库聚合标签（2026-09-15 增强） */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle>账号标签</CardTitle>
                <CardDescription>账号内容关键词与高频话题标签，将同时作为爆款爬取的推荐标签</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* 内容库匹配统计 */}
                {persona?.uid && (
                  <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                    内容库匹配 {persona?.library_match?.matched ?? 0} 条作品
                    {persona?.library_match?.transcribed ? `，已转录 ${persona.library_match.transcribed} 条` : '（尚无逐字稿，转录后可分析更全面）'}
                  </div>
                )}
                {/* LLM 标签提取（2026-09-15）：口播逐字稿无#话题，用 AI 提炼内容标签 */}
                {(persona?.library_match?.transcribed ?? 0) > 0 && (
                  <div className="rounded-md border p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-muted-foreground">逐字稿 AI 提炼标签</span>
                      <Button
                        size="sm"
                        variant={tagExtracting ? 'outline' : 'default'}
                        onClick={handleExtractTagsLlm}
                        disabled={tagExtracting}
                        className="h-7 px-2.5 text-xs"
                      >
                        {tagExtracting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                        {tagExtracting ? '提取中…' : 'AI 提取标签'}
                      </Button>
                    </div>
                    {tagExtractMsg && (
                      <p className={`mt-1.5 text-[11px] ${
                        tagExtractMsg.includes('失败') ? 'text-destructive' : 'text-muted-foreground'
                      }`}>
                        {tagExtractMsg}
                      </p>
                    )}
                    {tagExtractProgress && tagExtractProgress.total > 0 && (
                      <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary transition-all duration-500"
                          style={{ width: `${Math.round((tagExtractProgress.done / tagExtractProgress.total) * 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                )}
                {/* 内容关键词 */}
                <div>
                  <div className="mb-1.5 text-xs font-semibold text-muted-foreground">内容关键词</div>
                  {persona?.account_keywords?.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {persona.account_keywords.slice(0, 15).map((k, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 rounded-full bg-accent/40 px-2 py-0.5 text-[11px] font-medium"
                        >
                          {k.word}
                          <span className="text-[10px] text-muted-foreground">{k.count}</span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">暂无数据</span>
                  )}
                </div>
                {/* 内容标签（#话题） */}
                <div>
                  <div className="mb-1.5 text-xs font-semibold text-muted-foreground">内容标签</div>
                  {persona?.top_hashtags?.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {persona.top_hashtags.slice(0, 20).map((tag, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground"
                        >
                          #{tag.tag ?? tag}
                          <span className="text-[10px] opacity-70">×{tag.count ?? ''}</span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">暂无数据</span>
                  )}
                </div>
                {/* 内容库聚合标签（library_tags + 逐字稿提取，2026-09-15 新增） */}
                {persona?.library_tags?.length && (
                  <div>
                    <div className="mb-1.5 text-xs font-semibold text-muted-foreground">内容库标签</div>
                    <div className="flex flex-wrap gap-1.5">
                      {(persona.library_tags ?? []).slice(0, 20).map((tag, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-700 dark:text-emerald-400"
                        >
                          {tag.tag}
                          <span className="text-[10px] opacity-70">×{tag.count ?? ''}</span>
                        </span>
                      ))}
                    </div>
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      来自内容库该账号作品的标签与逐字稿话题聚合
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 六维雷达图（数据来自最近一次账号体检，2026-09-15 新增） */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle>六维画像</CardTitle>
                <CardDescription>来自最近一次账号体检报告</CardDescription>
              </CardHeader>
              <CardContent>
                {persona?.dimensions && Object.keys(persona.dimensions).some((k) => typeof persona.dimensions[k]?.score === 'number') ? (
                  <SixDimRadar dimensions={persona.dimensions} title="六维雷达图" />
                ) : (
                  <div className="flex h-56 flex-col items-center justify-center gap-2 rounded-lg border border-dashed text-center">
                    <p className="text-sm text-muted-foreground">暂无体检数据</p>
                    <p className="px-6 text-xs text-muted-foreground">
                      可先到「账号体检」生成报告，六维评分将展示在这里
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 直播电商 / 个人橱窗 */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle>电商能力</CardTitle>
                <CardDescription>账号的商业功能开通状态</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <span className="text-sm font-medium">直播电商</span>
                  {persona?.profile?.live_commerce ? (
                    <Badge className="bg-green-500/10 text-green-600">已开通</Badge>
                  ) : (
                    <Badge variant="outline">未开通</Badge>
                  )}
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <span className="text-sm font-medium">个人橱窗</span>
                  {persona?.profile?.with_fusion_shop_entry ? (
                    <Badge className="bg-green-500/10 text-green-600">已开通</Badge>
                  ) : (
                    <Badge variant="outline">未开通</Badge>
                  )}
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <span className="text-sm font-medium">全平台粉丝</span>
                  <span className="text-sm font-semibold">{fmt(persona?.profile?.mplatform_followers_count ?? 0)}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 近期话题 */}
          <Card>
            <CardHeader>
              <CardTitle>近期话题</CardTitle>
              <CardDescription>最近发布的作品主题</CardDescription>
            </CardHeader>
            <CardContent>
              {persona?.recent_topics?.length ? (
                <ul className="space-y-2">
                  {persona.recent_topics.map((topic, i) => (
                    <li key={i} className="text-sm text-muted-foreground">
                      {typeof topic === 'string' ? topic : topic.title || topic.name || JSON.stringify(topic)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">暂无话题数据</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── 表达习惯 ─── */}
      {activeTab === 'expression' && (
        <Card>
          <CardHeader>
            <CardTitle>表达习惯</CardTitle>
            <CardDescription>分析你的内容语言风格和表达偏好，用于后续 AI 改写时保持风格一致</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md border bg-muted/50 p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-muted-foreground">
                  自动分析：系统选取账号代表性作品，结合逐字稿提炼表达习惯与 IP 价值观
                </span>
                <Button
                  size="sm"
                  variant={extracting ? 'outline' : 'default'}
                  onClick={handleExtractExpression}
                  disabled={extracting || !persona?.uid}
                >
                  {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  {extracting ? '分析中…' : '从本人账号中提取'}
                </Button>
              </div>
              {extractMsg && (
                <p className={`mt-2 text-xs ${
                  extractMsg.includes('失败') || extractMsg.includes('未完成')
                    ? 'text-destructive'
                    : extractMsg.includes('完成')
                      ? 'text-emerald-600'
                      : 'text-muted-foreground'
                }`}>
                  {extractMsg}
                </p>
              )}
              {/* 逐字稿不足 → 一键转录提醒 + 进度条 */}
              {needTranscript && !transcribingAll && (
                <div className="mt-3 rounded-md border border-amber-400/40 bg-amber-400/10 p-3">
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    当前账号作品缺少逐字稿，自动分析无法进行。一键转录账号作品后可自动完成分析
                    （将下载视频并调用语音识别，逐条显示进度）。
                  </p>
                  <Button size="sm" className="mt-2" onClick={handleTranscribeAll}>
                    <Loader2 className="h-4 w-4" />
                    一键转录当前账号作品并分析
                  </Button>
                </div>
              )}
              {transcribingAll && (
                <div className="mt-3 rounded-md border bg-background p-3">
                  <p className="text-xs text-muted-foreground">{transcribeMsg}</p>
                  {transcribeProgress && transcribeProgress.total > 0 && (
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary transition-all duration-500"
                        style={{
                          width: `${Math.round((transcribeProgress.done / transcribeProgress.total) * 100)}%`,
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">表达风格描述</label>
              <textarea
                className="min-h-[200px] w-full rounded-md border bg-background p-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="输入你的表达习惯，例如：喜欢用第一人称、口语化、经常用反问句、常用表情符号……"
                value={expression}
                onChange={(e) => setExpressionState(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handleSaveExpression} disabled={savingExp}>
                {savingExp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                保存表达习惯
              </Button>
              <span className="text-xs text-muted-foreground">保存后，AI 改写将参考此风格</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── IP价值观 ─── */}
      {activeTab === 'memory' && (
        <Card>
          <CardHeader>
            <CardTitle>IP价值观</CardTitle>
            <CardDescription>定义你的 IP 核心价值主张，AI 在内容创作中将保持一致的价值立场</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">价值观描述</label>
              <textarea
                className="min-h-[200px] w-full rounded-md border bg-background p-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="输入你的核心价值主张，例如：帮助家长解决高考志愿填报焦虑、坚持用数据说话、不做标题党……"
                value={memory}
                onChange={(e) => setMemoryState(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handleSaveMemory} disabled={savingMem}>
                {savingMem ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                保存价值观
              </Button>
              <span className="text-xs text-muted-foreground">保存后，AI 内容创作将参考此价值观</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── 内容撰写设置 ─── */}
      {activeTab === 'rewrite' && (
        <Card>
          <CardHeader>
            <CardTitle>内容撰写设置</CardTitle>
            <CardDescription>配置 AI 改写参数，保存后内容库/内容中心的 AI 改写立即生效</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {rwLoading ? (
              <Skeleton className="h-40 rounded-lg" />
            ) : (
              <>
                {/* 创意度（温度） */}
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label className="text-sm font-medium">创意度（temperature）</label>
                    <span className="text-xs text-muted-foreground">{Number(rw.temperature ?? 0.7).toFixed(2)}</span>
                  </div>
                  <input type="range" min={0} max={1.2} step={0.05} value={rw.temperature ?? 0.7}
                    onChange={(e) => setRwField('temperature', parseFloat(e.target.value))} className="w-full" />
                </div>

                {/* 最大长度 */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium">最大长度（字符）</label>
                  <input type="number" min={100} max={8000} step={100} value={rw.max_chars ?? 2000}
                    onChange={(e) => setRwField('max_chars', parseInt(e.target.value) || 0)} className="h-9 w-40 rounded-md border bg-background px-2 text-sm" />
                </div>

                {/* 长度模式 */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium">长度模式</label>
                  <select value={rw.length_mode ?? 'similar'} onChange={(e) => setRwField('length_mode', e.target.value)}
                    className="h-9 rounded-md border bg-background px-2 text-sm">
                    <option value="similar">与原文相近</option>
                    <option value="short">精简（原文一半）</option>
                    <option value="long">扩展（原文1.5倍）</option>
                  </select>
                </div>

                {/* 开头模式 */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium">开头处理</label>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant={(rw.opening_mode ?? 'keep') === 'keep' ? 'default' : 'outline'} onClick={() => setRwField('opening_mode', 'keep')}>保持原开头</Button>
                    <Button type="button" size="sm" variant={(rw.opening_mode ?? 'keep') === 'auto' ? 'default' : 'outline'} onClick={() => setRwField('opening_mode', 'auto')}>AI 生成爆款开头</Button>
                  </div>
                  {(rw.opening_mode ?? 'keep') === 'keep' && (
                    <div className="mt-2">
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">保持开头前 {rw.opening_seconds ?? 3} 秒</span>
                      </div>
                      <input type="range" min={1} max={10} step={1} value={rw.opening_seconds ?? 3}
                        onChange={(e) => setRwField('opening_seconds', parseInt(e.target.value))} className="w-full" />
                    </div>
                  )}
                </div>

                {/* 原创度 */}
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label className="text-sm font-medium">原创度</label>
                    <span className="text-xs text-muted-foreground">{rw.originality ?? 70}%</span>
                  </div>
                  <input type="range" min={0} max={100} step={5} value={rw.originality ?? 70}
                    onChange={(e) => setRwField('originality', parseInt(e.target.value))} className="w-full" />
                </div>

                {/* 开关项 */}
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { key: 'use_expression', label: '融入表达习惯' },
                    { key: 'use_ip_memory', label: '融入IP价值观' },
                    { key: 'casual_style', label: '口语化表达' },
                    { key: 'use_hashtags', label: '添加话题标签' },
                    { key: 'keep_paragraphs', label: '保留分段结构' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center justify-between rounded-lg border p-3">
                      <span className="text-sm">{label}</span>
                      <input type="checkbox" checked={!!rw[key]} onChange={(e) => setRwField(key, e.target.checked)} className="h-4 w-4" />
                    </label>
                  ))}
                </div>

                <div className="flex items-center gap-3 border-t pt-4">
                  <Button onClick={handleSaveRewrite} disabled={rwSaving}>
                    {rwSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {rwSaving ? '保存中…' : '保存改写参数'}
                  </Button>
                  <Button variant="outline" onClick={handleResetRewrite} disabled={rwSaving}><RefreshCw className="h-4 w-4" />恢复默认</Button>
                  {rwSaved && <span className={`text-xs ${rwSaved === '保存失败' ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}`}>{rwSaved}</span>}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}